// src/components/Footer.js
import React, { useState } from 'react';
import './Footer.css';

const Footer = () => {
  const [formData, setFormData] = useState({
    serviceType: '',
    state: '',
    fullName: '',
    email: '',
    phone: '',
    website: '',
    reachVia: '',
    preferredTime: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  // Service Type Options
  const serviceTypes = [
    'Medical Billing',
    'Medical Coding',
    'Credentialing Services',
    'Denial Management',
    'AR Follow-up',
    'Revenue Cycle Management',
    'Consultation Services'
  ];

  // All US States
  const usStates = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
    'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
    'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
    'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
    'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
    'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
    'Wisconsin', 'Wyoming'
  ];

  // Reach Via Options
  const reachViaOptions = ['Email', 'Phone', 'Both'];

  // Preferred Time Options
  const timeOptions = ['Morning (9AM - 12PM)', 'Afternoon (12PM - 4PM)', 'Evening (4PM - 7PM)', 'Anytime'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      // Using Formspree (free service)
      const response = await fetch('https://formspree.io/f/xrejwqgl', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          serviceType: formData.serviceType,
          state: formData.state,
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          website: formData.website,
          reachVia: formData.reachVia,
          preferredTime: formData.preferredTime,
          message: formData.message
        })
      });

      if (response.ok) {
        setSubmitStatus({
          type: 'success',
          message: 'Thank you! Your consultation request has been sent. We will contact you shortly.'
        });
        
        // Reset form
        setFormData({
          serviceType: '',
          state: '',
          fullName: '',
          email: '',
          phone: '',
          website: '',
          reachVia: '',
          preferredTime: '',
          message: ''
        });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Something went wrong. Please try again or call us directly at 888-495-3786.'
      });
    } finally {
      setIsSubmitting(false);
      
      // Clear status message after 5 seconds
      setTimeout(() => {
        setSubmitStatus({ type: '', message: '' });
      }, 5000);
    }
  };

  return (
    <footer className="footer">
      <div className="footer-container">
        {/* Consultation Form Section */}
        <div className="consultation-section">
          <div className="consultation-header">
            <h2 className="consultation-title">Get a free Consultation</h2>
          </div>
          
          {/* Status Message */}
          {submitStatus.message && (
            <div className={`status-message ${submitStatus.type}`}>
              {submitStatus.message}
            </div>
          )}
          
          <form className="consultation-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Select Service Type *</label>
                <select name="serviceType" value={formData.serviceType} onChange={handleChange} required>
                  <option value="">Select a service</option>
                  {serviceTypes.map(service => (
                    <option key={service} value={service}>{service}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Select State *</label>
                <select name="state" value={formData.state} onChange={handleChange} required>
                  <option value="">Select a state</option>
                  {usStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Full Name *</label>
                <input type="text" name="fullName" placeholder="Enter your full name" value={formData.fullName} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input type="email" name="email" placeholder="Enter your email address" value={formData.email} onChange={handleChange} required />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Phone Number *</label>
                <input type="tel" name="phone" placeholder="(123) 456-7890" value={formData.phone} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Website (Optional)</label>
                <input type="url" name="website" placeholder="https://yourpractice.com" value={formData.website} onChange={handleChange} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Reach You Via *</label>
                <select name="reachVia" value={formData.reachVia} onChange={handleChange} required>
                  <option value="">Select preferred contact method</option>
                  {reachViaOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Preferred Time *</label>
                <select name="preferredTime" value={formData.preferredTime} onChange={handleChange} required>
                  <option value="">Select preferred time</option>
                  {timeOptions.map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label>Message</label>
                <textarea name="message" rows="4" placeholder="Tell us about your practice and requirements..." value={formData.message} onChange={handleChange}></textarea>
              </div>
            </div>

            <div className="form-row">
              <button type="submit" className="submit-btn" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Get Started →'}
              </button>
            </div>
          </form>
        </div>

        {/* Rest of your footer content remains the same */}
        <div className="footer-top">
          <div className="footer-top-content">
            <h2 className="footer-top-title">Let's Streamline Your Billing Together</h2>
            <p className="footer-top-description">
              Our experts help your practice boost collections and simplify billing. Our providers can bill for services, 
              products, or other items. We offer a variety of payment options to make it easy for you to pay your bills.
            </p>
          </div>
        </div>

        <div className="footer-main">
          <div className="footer-grid">
            {/* Contact Info Column */}
            <div className="footer-column">
              <h3 className="footer-column-title">Contact Info</h3>
              <div className="contact-info">
                <div className="contact-item-footer">
                  <div className="contact-icon">📍</div>
                  <div className="contact-details">
                    <strong>Head Office</strong>
                    <p>700 N Barton St Arlington, <br />United States, VA 22201</p>
                  </div>
                </div>
                <div className="contact-item-footer">
                  <div className="contact-icon">✉️</div>
                  <div className="contact-details">
                    <strong>Email Us</strong>
                    <p>info@oxymedbs.com</p>
                  </div>
                </div>
                <div className="contact-item-footer">
                  <div className="contact-icon">📞</div>
                  <div className="contact-details">
                    <strong>Call Us</strong>
                    <p>(757) 540-1740</p>
                    <p className="website-url">www.oxymedbs.com</p>
                  </div>
                </div>
              </div>
              
              {/* <div className="social-media">
                <h4 className="social-title">Follow our social media:</h4>
                <div className="social-icons">
                  <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Facebook">
                    <i className="fab fa-facebook-f"></i>
                  </a>
                  <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Twitter">
                    <i className="fab fa-twitter"></i>
                  </a>
                  <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Instagram">
                    <i className="fab fa-instagram"></i>
                  </a>
                </div>
              </div> */}
            </div>

            {/* Providers Care Column */}
            <div className="footer-column">
              <h3 className="footer-column-title">PROVIDERS CARE</h3>
              <p className="footer-column-description">
                We provide care to clients in need. We are committed to providing the best possible care to our patients. 
                We have a team of experienced professionals who are dedicated to providing the highest quality of care.
              </p>
              <div className="footer-links">
                <h4 className="links-title">Our Services</h4>
                <ul className="footer-list">
                  <li><a href="/services/medical-billing">Medical billing</a></li>
                  <li><a href="/services/collections">Collections</a></li>
                  <li><a href="/services/accounts-receivable">Accounts receivable</a></li>
                  <li><a href="/services/accounts-payable">Accounts payable</a></li>
                  <li><a href="/services/cash-flow-management">Cash flow management</a></li>
                  <li><a href="/services/financial-planning">Financial planning</a></li>
                </ul>
              </div>
            </div>

            {/* Our Specialties Column */}
            <div className="footer-column">
              <h3 className="footer-column-title">Our Specialties</h3>
              <ul className="footer-list specialties-list">
                <li><a href="/specialties/medical-billing">Medical billing services</a></li>
                <li><a href="/specialties/collections">Collections services</a></li>
                <li><a href="/specialties/accounts-receivable">Accounts receivable services</a></li>
                <li><a href="/specialties/accounts-payable">Accounts payable services</a></li>
                <li><a href="/specialties/cash-flow">Cash flow management services</a></li>
                <li><a href="/specialties/financial-planning">Financial planning services</a></li>
              </ul>
            </div>

            {/* About Company Column */}
            <div className="footer-column">
              <h3 className="footer-column-title">About Company</h3>
              <ul className="footer-list about-list">
                <li><a href="/about">About us</a></li>
                <li><a href="/about/history">Our history</a></li>
                <li><a href="/about/values">Our values</a></li>
                <li><a href="/contact">Contact us</a></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <div className="footer-bottom-content">
            <p className="copyright">
              &copy; {new Date().getFullYear()} Oxymed Billing Solutions. All rights reserved.
            </p>
            <div className="footer-bottom-links">
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/terms-and-conditions">Terms and Conditions</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;